import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import type { LiveRoom, Submissions } from '../rooms/api';
import { SimulcastLink } from '../rooms/Simulcast';
import { useLiveHosts } from '../rooms/useLiveHosts';

/** Three words for whether a room's door is open. See `OnAirList`. */
const DOOR: Record<Submissions, { label: string; tone: string }> = {
  open: { label: 'open', tone: 'text-fresh' },
  followers: { label: 'followers', tone: 'text-accent' },
  closed: { label: 'closed', tone: 'text-faint' },
};

/**
 * Who is on air, on the dashboard.
 *
 * ## The empty state is the common one
 *
 * Nobody is broadcasting most of the time, especially early, so the card that
 * says so is the one most people will see - which makes it worth writing
 * properly rather than treating as a fallback. It says what rooms *are*,
 * because somebody looking at an empty rail has not necessarily worked that
 * out, and it points at hosting, because the way this rail stops being empty is
 * somebody reading it and going live.
 *
 * It replaced a "coming soon" placeholder that held this slot while rooms were
 * unbuilt. The signature is unchanged, exactly as that file predicted.
 *
 * ## Four, and a count for the rest
 *
 * The rail is one column of a dashboard row, not a directory. Four fit without
 * scrolling; the header's live count opens the full list, and that is the one
 * place a complete list belongs.
 */
export function LiveHosts() {
  const { items, isPending } = useLiveHosts();

  return (
    <Card tone="hot" className="flex flex-col gap-5 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={`h-1.5 w-1.5 rounded-full bg-hot-text ${items.length > 0 ? 'animate-pulse' : ''}`}
            aria-hidden
          />
          <h2 className="meta-caps text-hot-text">Live hosts</h2>
        </div>
        {items.length > 0 && (
          <span className="meta-caps text-faint">
            {items.length === 1 ? '1 on air' : `${items.length} on air`}
          </span>
        )}
      </div>

      {isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-1 flex-col justify-center gap-3 py-4">
          <BroadcastGlyph />

          <p className="text-[15px] font-medium text-ink">Nobody is on air.</p>
          <p className="max-w-xs text-sm leading-relaxed text-soft">
            Hosts run live sets here, take submissions from anyone listening, and play unsigned
            records to whoever drops in. When someone goes live, they show up here.
          </p>

          {/*
            A real link, unlike the "notify me" button this card used to
            deliberately not have. `/host` exists, takes applications, and is
            the answer to an empty rail.
          */}
          <Link to="/host" className="text-sm text-accent hover:underline">
            Apply to host →
          </Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-live-hosts>
          {items.slice(0, 4).map((room) => (
            <li key={room.id}>
              <HostRow room={room} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function HostRow({ room }: { room: LiveRoom }) {
  const door = DOOR[room.submissions];

  return (
    <Link
      to={`/${room.username}`}
      data-live-host={room.username}
      className="flex items-center gap-3 rounded-[var(--radius-card)] border border-line p-2.5 transition-colors hover:border-hot-edge hover:bg-raised"
    >
      {room.now_playing?.artwork_small ? (
        <img
          src={room.now_playing.artwork_small}
          alt=""
          className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)] object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)]"
          style={{ backgroundImage: 'repeating-linear-gradient(45deg,var(--color-blank) 0 6px,var(--color-blank-alt) 6px 12px)' }}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] font-bold text-ink">{room.host.display_name}</span>
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-[10px] text-soft">
            {room.now_playing ? room.now_playing.title : 'between tracks'}
          </span>
          {/* A tag, not a link - the whole row is already one. Clicking
              through to the stream happens on the profile. */}
          <SimulcastLink simulcast={room.simulcast} size="sm" interactive={false} />
        </span>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-0.5 font-mono text-[10px]">
        <span className="text-faint">{room.listener_count} listening</span>
        {/* The door and the depth together, because "open" with forty waiting
            is a different invitation from "open" with none. */}
        <span className={door.tone}>
          {door.label} · {room.queue_count} queued
        </span>
      </div>
    </Link>
  );
}

function BroadcastGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-8 w-8 text-hot-text/60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
      <path d="M8.2 8.2a5.4 5.4 0 0 0 0 7.6M15.8 15.8a5.4 5.4 0 0 0 0-7.6" />
      <path d="M5.4 5.4a9.4 9.4 0 0 0 0 13.2M18.6 18.6a9.4 9.4 0 0 0 0-13.2" />
    </svg>
  );
}
